import dataTypesConfig from '../constants/dataTypes.json';
import { toWireInteger, fitsType } from './exactNumeric';
import { toScaledInteger, fromScaledInteger } from './decimalScale';

/**
 * An ISO 8601 time_point_ext as the node emits it, split into the part a
 * datetime-local input accepts and the precision it does not.
 *
 *   1  up to the minute, e.g. "-000044-03-15T12:00"
 *   2  ":SS" if present
 *   3  fractional digits if present
 *
 * The year takes an optional leading '-' and 4 to 6 digits. The previous
 * pattern demanded exactly four digits with no sign, so every BC date and every
 * year past 9999 fell through to an empty string — the row was on the chain and
 * correct, and the cell rendered blank.
 */
const TPE_ISO = /^(-?\d{4,6}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?(?:\.(\d{1,6}))?/;

export const typeConverters = {
    // Unsigned integer types
    // Široki celi brojevi NE prolaze kroz `Number` — vidi `exactNumeric.js`.
    // Čvor ih daje kao tačan string; ovde ostaju string do lanca.
    [dataTypesConfig.supportedTypes.UINT128_T]: {
        toDisplay: (value) => value !== null && value !== undefined ? String(value) : '',
        fromDisplay: (value) => value === '' ? null : toWireInteger(value),
        validate: (value) => fitsType(value, 'uint128_t')
    },
    [dataTypesConfig.supportedTypes.UINT64_T]: {
        toDisplay: (value) => value !== null && value !== undefined ? String(value) : '',
        fromDisplay: (value) => value === '' ? null : toWireInteger(value),
        validate: (value) => fitsType(value, 'uint64_t')
    },
    [dataTypesConfig.supportedTypes.UINT32_T]: {
        toDisplay: (value) => value !== null && value !== undefined ? String(value) : '',
        fromDisplay: (value) => value === '' ? null : Number(value),
        validate: (value) => !isNaN(value) && value !== null && value >= 0 && value <= 4294967295
    },
    [dataTypesConfig.supportedTypes.UINT16_T]: {
        toDisplay: (value) => value !== null && value !== undefined ? String(value) : '',
        fromDisplay: (value) => value === '' ? null : Number(value),
        validate: (value) => !isNaN(value) && value !== null && value >= 0 && value <= 65535
    },
    [dataTypesConfig.supportedTypes.UINT8_T]: {
        toDisplay: (value) => value !== null && value !== undefined ? String(value) : '',
        fromDisplay: (value) => value === '' ? null : Number(value),
        validate: (value) => !isNaN(value) && value !== null && value >= 0 && value <= 255
    },
    
    // Signed integer types
    [dataTypesConfig.supportedTypes.INT64_T]: {
        toDisplay: (value) => value !== null && value !== undefined ? String(value) : '',
        fromDisplay: (value) => value === '' ? null : toWireInteger(value),
        validate: (value) => fitsType(value, 'int64_t')
    },
    [dataTypesConfig.supportedTypes.INT32_T]: {
        toDisplay: (value) => value !== null && value !== undefined ? String(value) : '',
        fromDisplay: (value) => value === '' ? null : Number(value),
        validate: (value) => !isNaN(value) && value !== null && value >= -2147483648 && value <= 2147483647
    },
    [dataTypesConfig.supportedTypes.INT16_T]: {
        toDisplay: (value) => value !== null && value !== undefined ? String(value) : '',
        fromDisplay: (value) => value === '' ? null : Number(value),
        validate: (value) => !isNaN(value) && value !== null && value >= -32768 && value <= 32767
    },
    [dataTypesConfig.supportedTypes.INT8_T]: {
        toDisplay: (value) => value !== null && value !== undefined ? String(value) : '',
        fromDisplay: (value) => value === '' ? null : Number(value),
        validate: (value) => !isNaN(value) && value !== null && value >= -128 && value <= 127
    },
    
    // Floating point types
    [dataTypesConfig.supportedTypes.DOUBLE]: {
        toDisplay: (value) => value !== null && value !== undefined ? String(value) : '',
        fromDisplay: (value) => value === '' ? null : Number(value),
        validate: (value) => !isNaN(value) && value !== null
    },
    [dataTypesConfig.supportedTypes.FLOAT]: {
        toDisplay: (value) => value !== null && value !== undefined ? String(value) : '',
        fromDisplay: (value) => value === '' ? null : Number(value),
        validate: (value) => !isNaN(value) && value !== null
    },
    
    // Boolean type
    [dataTypesConfig.supportedTypes.BOOL]: {
        toDisplay: (value) => {
            // Handle various boolean formats from API
            if (value === true || value === 1 || value === '1' || value === 'true') return 'true';
            if (value === false || value === 0 || value === '0' || value === 'false') return 'false';
            return '';
        },
        fromDisplay: (value) => {
            if (value === 'true') return true;
            if (value === 'false') return false;
            return null;
        },
        validate: (value) => value === true || value === false
    },
    
    // Time point type
    //
    // The chain stores time_point_ext as signed microseconds and the node hands
    // it back as an ISO string in UTC. Nothing here goes through `new Date()`,
    // deliberately: Date carries a timezone and only reaches +/-285 years from
    // the epoch, while the type spans +/-294,000. Treating these strings as text
    // keeps the client agreeing with `dateToTimePointExt` on the backend.
    //
    // TPE_ISO covers what the node emits and what the inputs produce: optional
    // leading '-', a 4 to 6 digit year, and optional seconds/fraction.
    [dataTypesConfig.supportedTypes.TIME_POINT]: {
        // Anything after the minute is dropped, because <input type="datetime-local">
        // rejects a value it cannot round-trip. Use displayFull for read-only cells.
        toDisplay: (value) => {
            if (!value) return '';
            const match = String(value).match(TPE_ISO);
            return match ? match[1] : '';
        },
        // Full precision as stored, for cells that only render. The old display
        // path cut everything past the minute, so two rows a second apart looked
        // identical and microseconds were invisible.
        displayFull: (value) => {
            if (!value) return '';
            const m = String(value).match(TPE_ISO);
            if (!m) return String(value);
            const [, minute, seconds = '', fraction = ''] = m;
            const trimmed = fraction.replace(/0+$/, '');
            return minute + (seconds || ':00') + (trimmed ? '.' + trimmed : '');
        },
        // datetime-local yields YYYY-MM-DDTHH:mm; the backend parser wants
        // seconds. Same rule as normalizeIso in readData.js — keep them in step.
        fromDisplay: (value) => {
            if (!value) return null;
            const dateStr = String(value).trim();
            return /^-?\d{4,6}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dateStr)
                ? `${dateStr}:00`
                : dateStr;
        },
        validate: (value) => !value || TPE_ISO.test(String(value))
    },
    
    // String types
    [dataTypesConfig.supportedTypes.STRING]: {
        toDisplay: (value) => value || '',
        fromDisplay: (value) => value || null,
        validate: (value) => !!value
    },
    [dataTypesConfig.supportedTypes.NAME]: {
        toDisplay: (value) => value || '',
        fromDisplay: (value) => value || null,
        validate: (value) => !!value
    }
};

/**
 * Konvertor za jedno POLJE, a ne samo za tip.
 *
 * Tačne decimale se na lancu čuvaju kao ceo broj u najmanjoj jedinici, a broj
 * decimala stoji uz kolonu (`scale`). Ćelija zato mora znati i tip i polje —
 * `797868` bez toga izgleda kao osamsto hiljada, a jeste 7978.68.
 *
 * Bez `scale` vraća zatečeni konvertor, pa sve ostale kolone rade kao i pre.
 */
export const converterFor = (type, scale) => {
    const base = typeConverters[type] || typeConverters[dataTypesConfig.supportedTypes.STRING];
    if (!Number.isInteger(scale) || scale < 0) return base;

    return {
        toDisplay: (value) =>
            value === null || value === undefined || value === '' ? '' : fromScaledInteger(value, scale),
        // Unos se ne prekida izuzetkom dok korisnik kuca: neispravan zapis daje
        // `null`, a `validate` ga onda i prijavi. Isto radi `toWireInteger`.
        fromDisplay: (value) => {
            if (value === '' || value === null || value === undefined) return null;
            try {
                return toScaledInteger(value, scale);
            } catch {
                return null;
            }
        },
        // Stiže već skalirana vrednost, pa se proverava da staje u tip.
        validate: (value) => value !== null && value !== undefined && fitsType(value, type),
    };
};
